/* Nối App Tư vấn với dữ liệu Telesales trên máy: khách hẹn đã check-in → lượt tư vấn; kết quả ghi ngược về hồ sơ khách */
import dayjs from 'dayjs';
import { BRANCHES } from '../Telesales/dashboard/data';
import { codeLabel, dichVuOf, maskPhone, needFieldsOf, needValue, type LeadAppt, type LeadLocal } from '../Telesales/lead/logic';
import { DEP_ST, checkQuote, type Quote } from '../Telesales/lead/quote';
import { genderOf, nextCode, type ApiSvc, type Visit } from './logic';

/** SĐT để so khớp: chỉ chữ số, đầu 84 đổi về 0 ("+84 901…" = "0901…") */
export const phoneKey = (s?: string | null) => {
  const d = String(s ?? '').replace(/\D/g, '');
  return d.startsWith('84') && d.length >= 11 ? `0${d.slice(2)}` : d;
};

/** Báo giá online còn hiệu lực gần nhất (đã gửi khách) */
const liveQuote = (l: LeadLocal): Quote | undefined =>
  (l.quotes ?? []).find((q) => q.status === 'sent' || q.status === 'replied' || q.status === 'approved');

/** Báo giá mà lịch hẹn đặt theo (Telesales chọn khi đặt lịch); lịch không gắn báo giá thì lấy bản còn hiệu lực gần nhất */
export const quoteOfAppt = (l: LeadLocal, appt?: { apiApptId?: number; apptId?: string }): Quote | undefined => {
  const a = appt && l.appts.find((x) => (appt.apiApptId && x.serverId === appt.apiApptId) || (appt.apptId && x.id === appt.apptId));
  const linked = a?.quoteCode ? (l.quotes ?? []).find((q) => q.code === a.quoteCode && q.status !== 'cancelled') : undefined;
  return linked ?? liveQuote(l);
};

/** Ảnh chụp báo giá online + cọc mang sang lượt tư vấn (gồm chương trình ưu đãi, % giảm, lý do) */
const onlineOf = (q?: Quote): Pick<Visit, 'online' | 'onlineDeps'> => ({
  online: q ? {
    code: q.code, sentAt: q.sendAt, validUntil: q.validUntil, items: q.items, total: checkQuote(q.items, q.discount, q.reason).after, by: q.by,
    promo: q.promo, discount: q.discount || undefined, reason: q.reason?.trim() || undefined,
  } : undefined,
  onlineDeps: q?.deposit ? [{
    code: q.deposit.code, amount: q.deposit.amount, purpose: q.deposit.purpose, status: DEP_ST[q.deposit.status].label,
    received: q.deposit.status === 'received' || q.deposit.status === 'used',
  }] : undefined,
});

/** Phiếu nhu cầu, báo giá / cọc online Telesales đã ghi trên hồ sơ khách — mang sang lượt tư vấn */
export function teleExtras(l: LeadLocal, mang?: Visit['mang'], appt?: { apiApptId?: number; apptId?: string }): Pick<Visit, 'needs' | 'online' | 'onlineDeps'> & { mang?: Visit['mang'] } {
  const m = l.mang ?? mang;
  const n = l.needs ?? {};
  const needs = m ? needFieldsOf(m, dichVuOf(m, n)).map((f) => ({ label: f.label, value: needValue(f, n) })).filter((x) => x.value) : [];
  return { mang: m, needs: needs.length ? needs : undefined, ...onlineOf(quoteOfAppt(l, appt)) };
}

export interface JourneyItem { at: number; text: string; by: string; change?: boolean }
const fmt = (t: number) => dayjs(t).format('HH:mm DD/MM');

/**
 * Hành trình trước khi đến: Telesales đã làm gì với khách (cũ → mới), đánh dấu những gì thay đổi
 * (mã S, báo giá, dời lịch). Lịch trên hệ thống khác giờ Telesales đặt → có người đổi lịch ngoài Telesales.
 */
export function journeyOf(v: Pick<Visit, 'apiApptId' | 'apptId' | 'apptAt'>, l?: LeadLocal): JourneyItem[] {
  if (!l) return [];
  const out: JourneyItem[] = [...l.touches].reverse().map((t) => {
    if (t.kind === 'call') {
      return { at: t.at, by: t.by, text: `Gọi ${t.dir === 'in' ? 'đến' : 'đi'}${t.code ? ` · ${t.code} ${codeLabel(t.code).split(' →')[0]}` : ''}${t.text ? ` · "${t.text}"` : ''}` };
    }
    return { at: t.at, by: t.by, text: t.text ?? '', change: t.kind === 'stage' || /báo giá|dời|huỷ/i.test(t.text ?? '') };
  });
  const a = l.appts.find((x) => (v.apiApptId && x.serverId === v.apiApptId) || (v.apptId && x.id === v.apptId));
  if (a && v.apptAt && Math.abs(a.at - v.apptAt) >= 60_000) {
    out.push({ at: v.apptAt, by: 'Lễ tân / màn Lịch hẹn', change: true, text: `Lịch dời từ ${fmt(a.at)} → ${fmt(v.apptAt)} (đổi ngoài Telesales)` });
  }
  return out.filter((x) => x.text);
}

/** Lượt tư vấn từ lịch hẹn Telesales đã check-in hôm nay */
export function visitFromAppt(leadId: number, l: LeadLocal, a: LeadAppt, used: string[], now: number): Visit | null {
  const branch = BRANCHES.find((b) => b.name === a.branch);
  if (!branch || a.status !== 'arrived' || !dayjs(a.at).isSame(now, 'day')) return null;
  const n = l.needs ?? {};
  const needs = needFieldsOf(l.mang ?? a.mang, dichVuOf(l.mang ?? a.mang, n))
    .map((f) => ({ label: f.label, value: needValue(f, n) }))
    .filter((x) => x.value);
  const owner = a.telesale?.name ?? l.assign?.name ?? a.by;
  return {
    pageStaff: a.staff?.name,
    apptAt: a.at,
    id: `tele-${a.id}`,
    code: nextCode('LV', used, now),
    branchId: branch.id,
    createdAt: now,
    cust: { name: a.cust?.name || a.customer?.name || `KH-${leadId}`, code: a.cust?.code || `KH-${leadId}`, phone: maskPhone(a.cust?.phone) || '—', gender: genderOf(a.customer?.gender) },
    phoneDigits: phoneKey(a.cust?.phone) || undefined,
    mang: l.mang ?? a.mang,
    need: a.service || a.note || 'Theo lịch hẹn Telesales',
    source: 'Telesales',
    owner,
    visitNo: 1,
    checkInAt: now,
    status: 'waiting',
    needs: needs.length ? needs : undefined,
    ...onlineOf(quoteOfAppt(l, { apptId: a.id })),
    note: '',
    deposits: [],
    plans: [],
    leadId,
    apptId: a.id,
    // Lịch đã lên hệ thống: cùng mã với lịch API để kiosk check-in lịch này không tạo thêm lượt, và Chuyển POS ghi được giỏ
    apiApptId: a.serverId,
    ...(isExecType(a.type) ? { execOwner: {} } : {}),
    log: [{ at: now, text: `Check-in theo lịch hẹn Telesales ${dayjs(a.at).format('HH:mm')}`, by: 'Lễ tân' }],
  };
}

/** Loại lịch hẹn làm dịch vụ / thực hiện / tái khám → khách vào Hàng chờ thực hiện của Lễ tân, không cần phân tư vấn viên */
export const EXEC_RE = /thực hiện|dịch vụ|tái khám|liệu trình/i;
export const isExecType = (t: unknown) => EXEC_RE.test(String(t ?? ''));

/** Lịch hẹn đã check-in hôm nay chưa có lượt tư vấn */
export function newFromTele(byId: Record<number, LeadLocal>, visits: Visit[], now: number): Visit[] {
  const have = new Set(visits.map((v) => v.apptId).filter(Boolean));
  // Lịch đã lên hệ thống và đã có lượt từ kiosk (api-…) thì không tạo lượt thứ hai
  const haveApi = new Set(visits.map((v) => v.apiApptId).filter(Boolean));
  const used = visits.map((v) => v.code);
  const out: Visit[] = [];
  Object.entries(byId).forEach(([k, l]) => l.appts.forEach((a) => {
    if (have.has(a.id) || (a.serverId && haveApi.has(a.serverId))) return;
    const v = visitFromAppt(Number(k), l, a, used, now);
    if (v) { out.push(v); used.push(v.code); }
  }));
  return out;
}

export interface DayAppt { leadId: number; a: LeadAppt; name: string; code: string; phone: string }
/** Lịch hẹn Telesales trong ngày (bỏ lịch huỷ / đã dời) — màn Check-in của Lễ tân đọc danh sách này */
export function apptsOfDay(byId: Record<number, LeadLocal>, now: number): DayAppt[] {
  const out: DayAppt[] = [];
  Object.entries(byId).forEach(([k, l]) => l.appts.forEach((a) => {
    if (a.status === 'cancelled' || a.movedTo || !dayjs(a.at).isSame(now, 'day')) return;
    out.push({ leadId: Number(k), a, name: a.cust?.name || a.customer?.name || `KH-${k}`, code: a.cust?.code || `KH-${k}`, phone: maskPhone(a.cust?.phone) || '—' });
  }));
  return out.sort((x, y) => x.a.at - y.a.at);
}

/* ---------------- Lịch hẹn trên API (kiosk / Lễ tân check-in) ---------------- */

/** customer_tag_status của lịch hẹn: 3 = khách đã check-in, đang chờ (cùng cách đọc với màn Lễ tân ở /check-in) */
export const API_CHECKED_IN = 3;
const branchOfApi = (name?: string | null) => {
  const n = (name ?? '').trim().toLowerCase();
  const hit = n ? BRANCHES.find((b) => { const x = b.name.toLowerCase(); return x === n || x.startsWith(n) || n.startsWith(x); }) : undefined;
  // Chỉ còn một cơ sở: lịch ở cơ sở cũ trên API cũng về cơ sở này
  return hit ?? (BRANCHES.length === 1 ? BRANCHES[0] : undefined);
};
/** Mảng đoán theo tên dịch vụ — chờ API trả mảng của dịch vụ */
export const mangOfApi = (text: string): Visit['mang'] => (/tóc|hair|da đầu/i.test(text) ? 'Newhair'
  : /mũi|mí|hút mỡ|ngực|phẫu/i.test(text) ? 'PTTM' : 'Clinic');

/** Dịch vụ khách đặt trên lịch hẹn: giá, số lượng lấy theo dòng của lịch (pivot), thiếu thì giá niêm yết */
export const apiSvcsOf = (a: any): ApiSvc[] => (a?.services ?? []).filter((s: any) => s?.id).map((s: any) => ({
  id: Number(s.id),
  name: String(s.name ?? '').trim(),
  price: Number(s.pivot?.price ?? s.price) || 0,
  qty: Math.max(1, Number(s.pivot?.quantity) || 1),
  cat: s.category?.name,
}));

/**
 * Giờ khách check-in của lịch API. API chưa trả mốc check-in riêng nên lấy lúc lịch đổi trạng thái lần cuối
 * (updated_at — check-in là lần đổi gần nhất khi lượt được tạo); thiếu, khác ngày hoặc sau hiện tại thì lấy lúc tải.
 */
export const checkInAtOf = (a: any, now: number) => {
  const raw = a?.checked_in_at ?? a?.updated_at;
  if (!raw) return now;
  const t = dayjs(String(raw).replace(' ', 'T')).valueOf();
  return Number.isFinite(t) && t <= now && dayjs(t).isSame(now, 'day') ? t : now;
};

/**
 * Hồ sơ Telesales của lịch API. Kho hồ sơ lưu theo id khách kênh (như màn Khách tiềm năng):
 * ưu tiên hồ sơ có chính lịch này (serverId), rồi channel_customer_id / customer.id; customer_id để sau cùng.
 */
export function leadIdOfApi(a: any, byId: Record<number, LeadLocal>): number | undefined {
  const own = Object.entries(byId).find(([, l]) => l.appts.some((x) => x.serverId && x.serverId === a?.id));
  if (own) return Number(own[0]);
  return [a?.channel_customer_id, a?.customer?.id, a?.customer_id].map(Number).find((id) => id > 0 && byId[id]);
}

/** Lượt tư vấn từ lịch hẹn API khách đã check-in hôm nay */
export function visitFromApi(a: any, used: string[], now: number, byId: Record<number, LeadLocal> = {}): Visit | null {
  const branch = branchOfApi(a?.branch?.name);
  if (!branch || Number(a?.customer_tag_status) !== API_CHECKED_IN || !dayjs(a.scheduled_at).isSame(now, 'day')) return null;
  const services: string[] = (a.services ?? []).map((s: any) => s?.name).filter(Boolean);
  const need = services.join(', ') || a.note || 'Theo lịch hẹn';
  const tele = a.telesale?.fullname || a.telesale?.name;
  const cid = a.customer?.id ?? a.channel_customer_id;
  const phone = a.customer_phone || a.customer?.phone;
  // Mở app muộn thì khách check-in từ sáng vẫn hiện đúng thời gian chờ và xếp đúng thứ tự chia khách
  const at = checkInAtOf(a, now);
  // Khách có hồ sơ Telesales trên máy → mang theo nhu cầu, báo giá online
  const leadId = leadIdOfApi(a, byId);
  const lead = leadId ? byId[leadId] : undefined;
  const extra = lead ? teleExtras(lead, mangOfApi(need), { apiApptId: Number(a.id) }) : undefined;
  return {
    id: `api-${a.id}`,
    code: nextCode('LV', used, now),
    branchId: branch.id,
    createdAt: at,
    cust: {
      name: a.customer_name || a.customer?.fullname || 'Khách',
      code: a.customer?.customer_code || (cid ? `KH-${cid}` : a.code),
      phone: maskPhone(phone) || '—',
      gender: genderOf(a.customer_gender ?? a.customer?.gender),
    },
    phoneDigits: phoneKey(phone) || undefined,
    mang: extra?.mang ?? mangOfApi(need),
    need,
    source: tele ? 'Telesales' : 'Vãng lai',
    owner: tele,
    pageStaff: a.staff?.fullname || a.staff?.name || undefined,
    apptAt: a.scheduled_at ? dayjs(String(a.scheduled_at).replace(' ', 'T')).valueOf() : undefined,
    visitNo: 1,
    checkInAt: at,
    status: 'waiting',
    note: '',
    deposits: [],
    plans: [],
    apiApptId: a.id,
    apptSvcs: apiSvcsOf(a),
    ...(isExecType(a.type?.name ?? a.appointment_type?.name ?? a.appointment_type ?? a.type) ? { execOwner: {} } : {}),
    ...(lead ? { leadId, needs: extra?.needs, online: extra?.online, onlineDeps: extra?.onlineDeps } : {}),
    log: [{ at, text: `Check-in lịch hẹn ${a.code ?? `#${a.id}`} · hẹn ${dayjs(a.scheduled_at).format('HH:mm')}`, by: 'Kiosk / Lễ tân' }],
  };
}

/** Lịch API đã check-in hôm nay chưa có lượt tư vấn */
export function newFromApi(items: any[], visits: Visit[], now: number, byId: Record<number, LeadLocal> = {}): Visit[] {
  const have = new Set(visits.map((v) => v.apiApptId).filter(Boolean));
  const used = visits.map((v) => v.code);
  const out: Visit[] = [];
  items.forEach((a) => {
    if (have.has(a?.id)) return;
    const v = visitFromApi(a, used, now, byId);
    if (v) { out.push(v); used.push(v.code); }
  });
  return out;
}

/* ---------------- Khách thẻ liệu trình chuyển tư vấn (từ màn Trừ buổi) ---------------- */

export interface CardReferral {
  name: string;
  phone: string;
  code: string;
  /** Nhu cầu ngắn, VD "Thẻ Hifu 5 buổi đã dùng hết — tư vấn gói tiếp theo" */
  need: string;
  /** Tình trạng từng thẻ — hiện ở Phiếu nhu cầu */
  cards: { label: string; value: string }[];
  /** Mảng đoán theo tên gói / nhóm gói */
  hint: string;
}
/** Khách đã có lượt hôm nay đang chờ / đang tư vấn (so theo SĐT đầy đủ; không có SĐT thì không coi là trùng) */
export const activeVisitOf = (visits: Visit[], phone: string, now: number) => {
  const p = phoneKey(phone);
  if (!p) return undefined;
  return visits.find((v) => (v.status === 'waiting' || v.status === 'consulting') && dayjs(v.checkInAt ?? v.createdAt).isSame(now, 'day')
    && (v.phoneDigits ?? v.cardPhone) === p);
};
/** Lượt tư vấn cho khách thẻ liệu trình: vào hàng chờ ngay, chia tự động như khách check-in */
export function visitFromCard(r: CardReferral, branchId: string, used: string[], by: string, now: number): Visit {
  return {
    id: `card-${now.toString(36)}`,
    code: nextCode('LV', used, now),
    branchId,
    createdAt: now,
    cust: { name: r.name, code: r.code, phone: maskPhone(r.phone) || r.phone },
    cardPhone: phoneKey(r.phone),
    phoneDigits: phoneKey(r.phone) || undefined,
    mang: mangOfApi(r.hint),
    need: r.need,
    source: 'Khách cũ',
    owner: by,
    visitNo: 2,
    checkInAt: now,
    status: 'waiting',
    needs: r.cards,
    note: '',
    deposits: [],
    plans: [],
    log: [{ at: now, text: 'Chuyển từ quầy Trừ buổi liệu trình', by }],
  };
}
